import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { App } from './app/app';
import { ClerkProvider } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import constants from '@playcastdotio/GlobalConfig';
import * as Sentry from '@sentry/react';
import config from './config.json';
import { HelmetProvider } from 'react-helmet-async';
import { getPersistentPlayerId } from './api';
import { AuthWrapper } from './app/components/AuthWrapper';

// Redirect pavilion domains to playcast.io (respects environment)
const hostname = window.location.hostname;
const isLocalhost = hostname === 'localhost';
if (!isLocalhost && hostname.includes('pavilion')) {
  console.log('[Redirect] Pavilion domain detected, redirecting to playcast.io');

  const isLocal = constants.VERSION.endsWith('-local');
  const branch = constants.VERSION.split('-')[1]; // e.g., "1.15.0-dev" → "dev"

  let homeUrl = '';

  if (isLocal) {
    // Local: http://localhost:4204/
    homeUrl = 'http://localhost:4204/';
  } else {
    // Build URL based on branch
    switch (branch) {
      case 'dev':
        homeUrl = 'https://dev.marketing.playcast.io/';
        break;
      case 'dev2':
        homeUrl = 'https://dev2.marketing.playcast.io/';
        break;
      case 'prerelease':
        homeUrl = 'https://prerelease.marketing.playcast.io/';
        break;
      default:
        // Release: https://playcast.io/
        homeUrl = 'https://playcast.io/';
        break;
    }
  }

  console.log(`[Redirect] Redirecting to: ${homeUrl}`);
  window.location.href = homeUrl;
  // Stop execution to prevent rendering
  throw new Error('Redirecting to playcast.io');
}

// Check if we are pavilion website or not.
let isPavilionMode = false;
if (!isLocalhost) {
  isPavilionMode = hostname.includes('pavilion');
} else {
  isPavilionMode = config.PAVILION_MODE;
}

// Initialize Sentry immediately
if (!constants.VERSION.endsWith('-local')) {
  Sentry.init({
    release: constants.VERSION,
    environment: constants.ENVIRONMENT,
    dsn: config.SENTRY_DSN,
    normalizeDepth: 10,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.playcast\.io(?!.*clerk)/,
      /^https:\/\/.*\.gibber\.com(?!.*clerk)/,
      /^https:\/\/.*\.api\.gibber\.com/,
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  if (typeof window !== 'undefined') {
    (window as any).Sentry = Sentry;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
const PUBLISHABLE_KEY = constants.CLERK_PUBLIC_KEY;

// Move playerId initialization to after render
setTimeout(() => {
  getPersistentPlayerId();
}, 0);

// Consolidated color theme
const CLERK_THEME_COLORS = {
  background: '#020617',
  border: '#C7D2FE',
  hoverBackground: '#1B4366',
  primary: '#FBBF24',
  text: '#FFFFFF',
  textSecondary: '#E0E7FF',
  divider: '#A5B4FC',
  footerBackground: '#0F172A',
};

const style = document.createElement('style');
style.textContent = `
  :root {
    --clerk-bg: ${CLERK_THEME_COLORS.background};
    --clerk-border: ${CLERK_THEME_COLORS.border};
    --clerk-hover-bg: ${CLERK_THEME_COLORS.hoverBackground};
    --clerk-primary: ${CLERK_THEME_COLORS.primary};
    --clerk-text: ${CLERK_THEME_COLORS.text};
    --clerk-text-secondary: ${CLERK_THEME_COLORS.textSecondary};
    --clerk-divider: ${CLERK_THEME_COLORS.divider};
    --clerk-footer-bg: ${CLERK_THEME_COLORS.footerBackground};
  }

  .cl-phoneInputBox {
    padding: 0 1rem !important;
    gap: 0 !important;
    display: flex !important;
    align-items: center !important;
  }
  .cl-phoneInputBox > button {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 0 !important;
    background: transparent !important;
  }
  .cl-phoneInputBox > input {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 0 0 0 0.5rem !important;
    background: transparent !important;
  }
  .cl-phoneInputBox button,
  .cl-phoneInputBox input {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .cl-phoneInputBox *:not(.cl-phoneInputBox) {
    border: none !important;
    box-shadow: none !important;
  }
  .cl-selectButton__countryCodeList,
  .cl-internal-b3fm6y {
    background: var(--clerk-bg) !important;
    border: 1px solid var(--clerk-border) !important;
  }
  .cl-selectOption,
  [role="option"] {
    background: var(--clerk-bg) !important;
    color: white !important;
  }
  .cl-selectOption:hover,
  [role="option"]:hover {
    background: var(--clerk-hover-bg) !important;
  }
  .cl-selectOption[data-selected="true"],
  [role="option"][data-selected="true"],
  [aria-selected="true"] {
    background: var(--clerk-hover-bg) !important;
  }
  input[placeholder*="Search"] {
    background: var(--clerk-bg) !important;
    color: white !important;
    border: 1px solid var(--clerk-border) !important;
  }
`;
document.head.appendChild(style);

const clerkAppearance = {
  baseTheme: dark,
  elements: {
    formFieldInput: 'text-white text-sm !bg-[color:var(--clerk-bg)] !py-6 rounded-lg !border !border-[color:var(--clerk-border)]',
    formFieldLabel: 'text-white',
    formFieldInputShowPasswordButton: 'text-[color:var(--clerk-divider)]',
    phoneInputBox: 'text-white text-sm !bg-[color:var(--clerk-bg)] !rounded-lg !border !border-[color:var(--clerk-border)]',
    selectButton: '!bg-[color:var(--clerk-bg)] text-white',
    selectButton__countryCode: 'text-white',
    phoneInput: 'text-white text-sm !bg-[color:var(--clerk-bg)]',
    otpCodeFieldInput: 'text-gray-900 text-sm',
    socialButtonsIconButton: '!bg-[color:var(--clerk-text-secondary)] !border-none !py-4',
    dividerLine: '!bg-[color:var(--clerk-divider)] h-0.5',
    dividerText: 'text-[color:var(--clerk-divider)]',
    footer: '!bg-[color:var(--clerk-footer-bg)] font-semibold',
    footerActionText: '!text-[color:var(--clerk-text-secondary)] font-semibold',
    footerActionLink: '!text-[color:var(--clerk-primary)] hover:!text-[color:var(--clerk-primary)] hover:opacity-80',
    formHeaderTitle: 'text-white',
    formHeaderSubtitle: 'text-[color:var(--clerk-text-secondary)]',
  },
  variables: {
    colorBackground: CLERK_THEME_COLORS.background,
    colorPrimary: CLERK_THEME_COLORS.primary,
    colorText: CLERK_THEME_COLORS.text,
    colorTextOnPrimaryBackground: CLERK_THEME_COLORS.background,
    colorTextSecondary: CLERK_THEME_COLORS.textSecondary,
    colorInputBackground: CLERK_THEME_COLORS.background,
    colorInputText: CLERK_THEME_COLORS.text,
  },
};

// Compute redirect URLs - exclude electron auth route
const isElectronAuth = window.location.pathname === '/host-login';
const redirectUrl = isElectronAuth ? undefined : window.location.href;

root.render(
  <StrictMode>
    <HelmetProvider>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        appearance={clerkAppearance}
        {...(redirectUrl && {
          afterSignOutUrl: redirectUrl,
          signInForceRedirectUrl: redirectUrl,
          signUpForceRedirectUrl: redirectUrl,
        })}
      >
        <AuthWrapper>
          <App isPavilionMode={isPavilionMode} />
        </AuthWrapper>
      </ClerkProvider>
    </HelmetProvider>
  </StrictMode>,
);

